// Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms and the SOW between the parties dated 10/31/2024.

import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, Subject, Observer, BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ChatEligibilityService } from './chat-eligibility.service';
import { CloudWatchRumService } from './cloudwatch-rum.service';
import { v4 as uuidv4 } from 'uuid';

export interface ChatResponse {
  type: string;
  message: string;
  timestamp: string | number;
}

export enum ConnectionStatus {
  CONNECTED = 'CONNECTED',
  CONNECTING = 'CONNECTING',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
  OFFLINE = 'OFFLINE'
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private subject: Subject<MessageEvent> | null = null;
  private messageSubject = new BehaviorSubject<ChatResponse | null>(null);
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  private networkSubscriptions: Subscription = new Subscription();
  private reconnectTimeout: any;
  public isReconnecting: boolean = false;
  private sessionId: string = '';
  
  // Observable for components to subscribe to incoming messages
  public messages$ = this.messageSubject.asObservable();
  // Observable for connection status updates
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private ngZone: NgZone, private chatEligibilityService: ChatEligibilityService, private cloudWatchRumService: CloudWatchRumService) {
    // Initialize the connection status based on current online status
    this.updateNetworkStatus();
    
    // Setup listeners for online/offline events
    this.setupNetworkListeners();
    
    // Generate initial session ID
    this.generateSessionId();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.networkSubscriptions.unsubscribe();
    
    // Clear any pending timeouts
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    // Disconnect the WebSocket
    this.disconnect();
  }

  // Generate a new unique session ID using UUID v4
  private generateSessionId(): void {
    this.sessionId = uuidv4();
    this.logInfo(`Generated new session ID: ${this.sessionId}`);
  }

  // Connect to WebSocket server with a unique session ID
  public connect(url: string = environment.websocketUrl): Subject<MessageEvent> {
    // If we're offline or user is not eligible for chat, don't attempt to connect
    if (!navigator.onLine || !this.chatEligibilityService.isChatEligible()) {
      this.connectionStatusSubject.next(!navigator.onLine ? ConnectionStatus.OFFLINE : ConnectionStatus.ERROR);
      return this.getOrCreateSubject();
    }
    
    // Update status to connecting
    this.connectionStatusSubject.next(ConnectionStatus.CONNECTING);
    
    // Set reconnecting flag to true when starting connection
    this.isReconnecting = true;
    
    // Generate a new session ID for each new connection
    this.generateSessionId();
    this.logInfo(`Connecting with session ID: ${this.sessionId}`);
    
    if (!this.subject) {
      // Include session ID in the connection URL
      const urlWithParams = `${url}?authorization=${localStorage.getItem('id_token') || ''}&sessionId=${this.sessionId}`;
      this.subject = this.create(urlWithParams);
      
      // Subscribe to WebSocket messages
      this.subject.subscribe({
        next: (event: MessageEvent) => this.handleIncomingMessage(event),
        error: (error) => {
          console.error('WebSocket error:', error);
          this.connectionStatusSubject.next(ConnectionStatus.ERROR);
        }
      });
    }
    return this.subject;
  }

  // Close WebSocket connection
  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.subject = null;
      
      // Set the status based on network availability
      if (navigator.onLine) {
        this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
      } else {
        this.connectionStatusSubject.next(ConnectionStatus.OFFLINE);
      }
      this.isReconnecting = true; // Set flag when disconnecting for reconnection
    }
  }

  // Send message through WebSocket with specified format and session ID
  public sendMessage(message: string): void {
    // Don't attempt to send if we're offline or user is not eligible for chat
    if (!navigator.onLine || !this.chatEligibilityService.isChatEligible()) {
      if (!navigator.onLine) {
        console.warn('Cannot send message: device is offline');
        this.connectionStatusSubject.next(ConnectionStatus.OFFLINE);
      } else {
        console.warn('Cannot send message: AI feature is disabled or user not eligible');
        this.connectionStatusSubject.next(ConnectionStatus.ERROR);
      }
      return;
    }
    
    // Record RUM custom event for chat message submission
    this.cloudWatchRumService.addCustomEvent('chat_message_submitted', {
      messageLength: message.length,
      sessionId: this.sessionId,
      timestamp: Date.now()
    });
    
    const payload = {
      action: 'default',
      message: message,
      sessionId: this.sessionId // Include session ID in every message
    };
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    } else {
      console.error('WebSocket is not connected.', this.socket?.readyState);
      // Try to reconnect
      this.reconnect();
    }
  }
  
  // Setup network state listeners
  private setupNetworkListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const online$ = fromEvent(window, 'online');
      const offline$ = fromEvent(window, 'offline');
      
      const onlineSub = online$.subscribe(() => {
        this.ngZone.run(() => {
          this.logInfo('Device is online');
          this.updateNetworkStatus();
          // Attempt to reconnect if we were previously connected
          if (this.socket === null) {
            this.reconnect();
          }
        });
      });
      
      const offlineSub = offline$.subscribe(() => {
        this.ngZone.run(() => {
          this.logInfo('Device is offline');
          this.updateNetworkStatus();
        });
      });
      
      this.networkSubscriptions.add(onlineSub);
      this.networkSubscriptions.add(offlineSub);
    });
  }
  
  // Update connection status based on network state
  private updateNetworkStatus(): void {
    if (!navigator.onLine) {
      this.logInfo('Network is offline, updating connection status');
      this.connectionStatusSubject.next(ConnectionStatus.OFFLINE);
    } else if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.connectionStatusSubject.next(ConnectionStatus.CONNECTED);
    } else if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
      this.connectionStatusSubject.next(ConnectionStatus.CONNECTING);
    } else {
      this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
    }
  }

  // Send chat history to get a response
  public sendChatHistory(messages: any[]): void {
    // Get the last user message
    const lastUserMessage = messages
      .filter(msg => msg.role === 'user')
      .pop();
    
    if (lastUserMessage) {
      this.sendMessage(lastUserMessage.content);
    } else {
      console.error('No user message found in chat history');
    }
  }

  // Process incoming WebSocket messages
  private handleIncomingMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      
      // Handle message in the expected format { type: 'SUCCESS', message: 'response from server', timestamp: Date obj }
      if (data.type === 'SUCCESS' && data.message) {
        // Record RUM custom event for chat message received
        this.cloudWatchRumService.addCustomEvent('chat_message_received', {
          messageType: data.type,
          sessionId: this.sessionId,
          timestamp: data.timestamp || Date.now()
        });
        
        this.messageSubject.next({
          type: data.type,
          message: data.message,
          timestamp: data.timestamp || Date.now()
        });
      } else {
        console.warn('Received message in unexpected format:', data);
        this.messageSubject.next({
          type: data.type,
          message: data.message,
          timestamp: data.timestamp || Date.now()
        });
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error, event.data);
      this.messageSubject.next({
        type: 'ERROR',
        message: 'Error parsing message',
        timestamp: Date.now()
      });
    }
  }
  
  // Try to reconnect if connection is lost
  private reconnect(): void {
    if (!navigator.onLine || !this.chatEligibilityService.isChatEligible()) {
      if (!navigator.onLine) {
        this.logInfo('Cannot reconnect: device is offline');
        this.connectionStatusSubject.next(ConnectionStatus.OFFLINE);
      } else {
        this.logInfo('Cannot reconnect: AI feature is disabled or user not eligible');
        this.connectionStatusSubject.next(ConnectionStatus.ERROR);
      }
      return;
    }
    
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this.logInfo('Attempting to reconnect WebSocket...');
      
      // Clear any existing timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
      }
      
      this.isReconnecting = true;
      
      // Reconnect after a short delay with a new session ID
      this.reconnectTimeout = setTimeout(() => {
        // Generate new session ID for reconnection
        this.generateSessionId();
        this.logInfo(`Reconnecting with new session ID: ${this.sessionId}`);
        
        this.subject = null; // Reset subject to force new connection
        this.connect();
        this.isReconnecting = false;
      }, 3000);
    }
  }
  
  // Create a new WebSocket subject for communication or return the existing one
  private getOrCreateSubject(): Subject<MessageEvent> {
    if (!this.subject) {
      this.subject = new Subject<MessageEvent>();
    }
    return this.subject;
  }

  // Create a new WebSocket subject for communication
  private create(url: string): Subject<MessageEvent> {
    this.socket = new WebSocket(url);

    const observable = new Observable((obs: Observer<MessageEvent>) => {
      this.socket!.onmessage = obs.next.bind(obs);
      this.socket!.onerror = obs.error.bind(obs);
      this.socket!.onclose = () => {
        this.logInfo(`WebSocket connection closed for session: ${this.sessionId}`);
        obs.complete();
        
        // Update status based on network availability
        if (navigator.onLine) {
          this.connectionStatusSubject.next(ConnectionStatus.DISCONNECTED);
          
          // Try to reconnect after a short delay
          if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
          }
          
          this.reconnectTimeout = setTimeout(() => this.reconnect(), 3000);
        } else {
          this.connectionStatusSubject.next(ConnectionStatus.OFFLINE);
        }
      };
      
      this.socket!.onopen = () => {
        this.logInfo(`WebSocket connection established for session: ${this.sessionId}`);
        this.connectionStatusSubject.next(ConnectionStatus.CONNECTED);
        this.isReconnecting = false; // Reset reconnecting flag on successful connection
      };
      
      return this.socket!.close.bind(this.socket);
    });

    const observer = {
      next: (data: any) => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(data));
        }
      },
      error: (err: any) => console.error(err),
      complete: () => {
        if (this.socket) {
          this.socket.close();
        }
      }
    };

    return Subject.create(observer, observable);
  }

  // Check if WebSocket is connected
  public isConnected(): boolean {
    return navigator.onLine && this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

    /**
   * Log helper methods that only log in non-production environments
   */
  private logInfo(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.log(`[WebSocketService] ${message}`, ...data);
    }
  }

  // Get current session ID
  public getSessionId(): string {
    return this.sessionId;
  }
}